import { useEffect, useState } from 'react';

import { isCancellation } from '../../lib/is-cancellation.js';
import { isNetworkError } from '../../lib/is-network-error.js';
import { watchPageLeave } from '../../lib/page-leave.js';
import { reportConsultError } from '../../lib/report-consult-error.js';
import type { ConsultTab } from '../consult-tabs.js';
import { getVisibleTabs } from '../consult-tabs.js';
import { notifyParent } from '../embed/embedded.js';
import type { ConsultTaskAssignee } from '../one-to-one/task-assignee.js';
import { isRdPoolTaskAssigneePlaceholderFirstName } from '../one-to-one/task-assignee.js';

import type {
  ConsultMemberDetails,
  ConsultStage,
  ConsultType,
  ExchangeResult,
} from './exchange-types.js';
import { isConsultType } from './exchange-types.js';

export type ConsultExchangeStatus = 'loading' | 'exchange-failed' | 'ready';

export type ConsultExchange = {
  jwt: string | null;
  setJwt: (jwt: string) => void;
  status: ConsultExchangeStatus;
  error: string | null;
  exchangeFailTitle: string;
  /**
   * Whether the failure is one the member can act on by retrying — true only when the request
   * never reached the server. An expired token re-fails identically however many times it is sent.
   */
  exchangeFailRetryable: boolean;
  consultType: ConsultType | undefined;
  consultStage: ConsultStage | undefined;
  memberDetails: ConsultMemberDetails | null;
  taskAssignee: ConsultTaskAssignee | null;
  activeTab: ConsultTab;
  setActiveTab: (tab: ConsultTab) => void;
  /** Task 10: gates the 1:1 tab behind `AdminFeeCardStep` until a card is on file. */
  adminFeeCardRequired: boolean;
  /**
   * Re-runs the magic-link exchange, so a freshly-recorded admin-fee Observation can flip
   * `adminFeeCardRequired` to `false` without the member re-visiting the link.
   *
   * @remarks Safe to call repeatedly: `ExchangeLinkUseCase` performs no `consumedAt` check and no
   * claim, so the magic link is not single-use. If that ever changes, this call site strands a
   * member who just paid on a dead link.
   */
  reExchange: () => void;
};

/**
 * Exchanges the magic-link token for a session JWT and everything the page branches on:
 * consult type and stage, member identity, task assignee, and the failure copy.
 *
 * @remarks `activeTab` lives here because the exchange payload picks it — `consultType` decides
 * which tabs exist, so the first visible tab is selected in the same `.then` that resolves the
 * type. Tab clicks then move it through `setActiveTab`.
 */
export function useConsultExchange(params: {
  token: string | undefined;
  apiBase: string;
}): ConsultExchange {
  const { token, apiBase } = params;
  const [jwt, setJwt] = useState<string | null>(null); // Used for event registration
  const [status, setStatus] = useState<ConsultExchangeStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [exchangeFailTitle, setExchangeFailTitle] = useState('Link Expired');
  const [exchangeFailRetryable, setExchangeFailRetryable] = useState(false);
  const [consultType, setConsultType] = useState<ConsultType | undefined>(undefined);
  const [consultStage, setConsultStage] = useState<ConsultStage | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<ConsultTab>('group');
  /** Member details for webinar registration; set from exchange when backend provides them. */
  const [memberDetails, setMemberDetails] = useState<ConsultMemberDetails | null>(null);
  const [taskAssignee, setTaskAssignee] = useState<ConsultTaskAssignee | null>(null);
  const [adminFeeCardRequired, setAdminFeeCardRequired] = useState(false);
  // Bumped by `reExchange` to re-run the effect below without `token` itself changing.
  const [exchangeAttempt, setExchangeAttempt] = useState(0);

  useEffect(() => {
    if (!token) {
      setStatus('exchange-failed');
      setExchangeFailTitle('Link Expired');
      setError('Invalid link');
      notifyParent({ type: 'nrx-consult:error' });
      return;
    }

    const controller = new AbortController();
    // Guarded rather than an unconditional `setStatus('loading')`: `reExchange` is also how
    // `AdminFeeCardStep` re-checks the gate after a card is stored, and that runs from `'ready'`,
    // where this would flash the loading state over a working page.
    setStatus((current) => (current === 'exchange-failed' ? 'loading' : current));
    const load = fetch(`${apiBase}/link/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      signal: controller.signal,
    })
      .then((res) => {
        if (res.status === 503) {
          throw new Error('EXCHANGE_SERVICE_UNAVAILABLE');
        }
        if (!res.ok) throw new Error('Invalid or expired link');
        return res.json() as Promise<ExchangeResult>;
      })
      .then((data) => {
        setJwt(data.jwt);
        // Validated beside its already-validated siblings. Resolved ONCE and reused for
        // `getVisibleTabs` below, so the tab selection and the stored type cannot disagree — passing
        // the raw wire value to one and the validated value to the other would reintroduce the gap
        // in a subtler form.
        const resolvedConsultType = isConsultType(data.consultType) ? data.consultType : undefined;
        setConsultType(resolvedConsultType);
        setConsultStage(
          data.consultStage === 'initial' || data.consultStage === 'follow-up'
            ? data.consultStage
            : undefined,
        );
        // `.trim() !== ''` on all three, matching the `taskAssignee` guard immediately below.
        //
        // With `typeof === 'string'` alone, an empty string passed and became `memberDetails`.
        // `register-webinar-modal.tsx` derives `fieldsLocked = Boolean(memberDetails)` and passes
        // `disabled={fieldsLocked}` AND `required={!fieldsLocked}` to email, firstName and lastName;
        // `use-webinar-registration.ts` then submits `{ ...memberDetails, phone }` and gates on
        // those same empty values. A member whose backend record had a blank name or email pressed
        // Register, saw "Please fill in email, first name, last name…", and could not edit any of the
        // three because all three were disabled. Group registration became impossible with no path
        // forward. Falling through to `null` gives them an editable, submittable form instead.
        if (
          data.member &&
          typeof data.member.email === 'string' &&
          data.member.email.trim() !== '' &&
          typeof data.member.firstName === 'string' &&
          data.member.firstName.trim() !== '' &&
          typeof data.member.lastName === 'string' &&
          data.member.lastName.trim() !== ''
        ) {
          const phoneFromMember =
            typeof data.member.phone === 'string' ? data.member.phone.trim() : '';
          setMemberDetails({
            email: data.member.email.trim(),
            firstName: data.member.firstName.trim(),
            lastName: data.member.lastName.trim(),
            ...(phoneFromMember ? { phone: phoneFromMember } : {}),
          });
        }
        if (
          data.taskAssignee &&
          typeof data.taskAssignee.firstName === 'string' &&
          data.taskAssignee.firstName.trim() !== '' &&
          typeof data.taskAssignee.scheduleUrl === 'string' &&
          data.taskAssignee.scheduleUrl.trim() !== ''
        ) {
          const firstName = data.taskAssignee.firstName.trim();
          const scheduleUrl = data.taskAssignee.scheduleUrl.trim();
          if (isRdPoolTaskAssigneePlaceholderFirstName(firstName)) {
            setTaskAssignee(null);
          } else {
            setTaskAssignee({ firstName, scheduleUrl });
          }
        } else {
          setTaskAssignee(null);
        }
        setAdminFeeCardRequired(data.adminFeeCardRequired === true);
        const tabs = getVisibleTabs(resolvedConsultType);
        setActiveTab(tabs[0]);
        setStatus('ready');
      });
    const pageLeave = watchPageLeave(load);
    load.catch((err: unknown) => {
      // The signal, not the error name: Safari calls a cancelled load `TypeError: Load
      // failed`, which is indistinguishable from a real failure by wording alone.
      if (isCancellation(err, controller.signal)) return;
      // Reported for every failure but a page leave, including the expected "invalid or expired
      // link" — an operator cannot tell a member who waited too long from a broken exchange without
      // it, and the tag plus the error message is what separates them. No member context (W6).
      if (!pageLeave.leaving) reportConsultError('link-exchange', err);
      setStatus('exchange-failed');
      notifyParent({ type: 'nrx-consult:error' });
      // A request that never reached the server says nothing about the token, so this must not
      // fall through to the expired-link copy — that tells the member to request a new link and
      // routes them to support for something a retry fixes.
      if (isNetworkError(err)) {
        setExchangeFailTitle('Connection problem');
        setError("We couldn't reach NourishedRx. Check your connection and try again.");
        setExchangeFailRetryable(true);
        return;
      }
      const unavailable = err instanceof Error && err.message === 'EXCHANGE_SERVICE_UNAVAILABLE';
      setExchangeFailTitle(unavailable ? 'Temporarily unavailable' : 'Link Expired');
      setError(
        unavailable
          ? 'Scheduling is temporarily unavailable. Please try again in a few minutes.'
          : 'This link is invalid or has expired. Please request a new one.',
      );
      setExchangeFailRetryable(false);
    });

    return () => controller.abort();
    // Vendor dependency list otherwise unchanged: `apiBase` is a module-level constant on the
    // page. `exchangeAttempt` is added so `reExchange` (Task 10: re-check the admin-fee gate
    // after a card is authorized) can re-run this effect without `token` itself changing.
  }, [token, exchangeAttempt]);

  return {
    jwt,
    setJwt,
    status,
    error,
    exchangeFailTitle,
    exchangeFailRetryable,
    consultType,
    consultStage,
    memberDetails,
    taskAssignee,
    activeTab,
    setActiveTab,
    adminFeeCardRequired,
    reExchange: () => setExchangeAttempt((n) => n + 1),
  };
}
